import React from 'react';

const Spinner = () => (
  <svg className="animate-spin h-4 w-4 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const BASE =
  'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-canvas disabled:opacity-50 disabled:pointer-events-none select-none';

const VARIANTS = {
  primary: 'bg-accent text-white dark:text-slate-900 hover:bg-accent-hover shadow-sm',
  secondary: 'bg-surface-2 text-content hover:bg-surface-3',
  outline: 'border border-edge-strong bg-surface text-content hover:bg-surface-2',
  ghost: 'text-content-secondary hover:bg-surface-2 hover:text-content',
  danger: 'bg-red-600 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-400 dark:text-white shadow-sm',
  success: 'bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-400 shadow-sm',
};

const SIZES = {
  xs: 'h-8 px-2.5 text-xs',
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
  // Icon-only squares. Pass the icon as CHILDREN (any glyph size), not via the
  // icon prop (which pins 16px). 'icon' is the 40px touch-safe default;
  // 'icon-sm' for dense desktop-only toolbars.
  icon: 'h-10 w-10 p-0',
  'icon-sm': 'h-9 w-9 p-0',
};

export default function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon: Icon,
  iconEnd: IconEnd,
  flipIconRtl = false,
  fullWidth = false,
  as: Comp = 'button',
  type,
  className = '',
  children,
  disabled,
  onClick,
  ...rest
}) {
  const isButton = Comp === 'button';
  const isDisabled = Boolean(disabled || loading);
  const iconClassName = `shrink-0${flipIconRtl ? ' rtl:-scale-x-100' : ''}`;

  const handleClick = (e) => {
    if (isDisabled) {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      return;
    }
    onClick?.(e);
  };

  return (
    <Comp
      className={`${BASE} ${VARIANTS[variant] || VARIANTS.primary} ${SIZES[size] || SIZES.md} ${fullWidth ? 'w-full' : ''} ${className}`}
      disabled={isButton ? isDisabled : undefined}
      aria-disabled={!isButton && isDisabled ? true : undefined}
      type={isButton ? type || 'button' : undefined}
      onClick={handleClick}
      {...rest}
    >
      {loading ? <Spinner /> : Icon && <Icon size={16} className={iconClassName} />}
      {children}
      {IconEnd && <IconEnd size={16} className={iconClassName} />}
    </Comp>
  );
}
